import { VEHICLE_SPEED_SCALE } from './hover_physics.js';
/**
 * hovercraft_catalog.ts — crafted ground vehicles: five hovercraft models,
 * tiered. CLIENT MIRROR of server/src/lib/hovercraft_catalog.ts (keep the
 * tables byte-identical — the client integrates the ride and the server
 * enforces the speed cap).
 *
 * Every model has DISTINCT stats, and speed tracks crafting cost: higher
 * tiers take deeper component trees, rarer families and bigger quantities
 * (see schematic_catalog — the cost law). Crafted QUALITY matters: top speed
 * scales 0.9×..1.2× across the 0..1000 range, so a master-crafted skimmer
 * visibly outruns a bench copy of the same design.
 *
 * SPEED LAW (2026-09-01): every ground vehicle cruises at 150 ± 40 m/s —
 * the catalog tiers span 110..190 and `hovercraftSpeed` CLAMPS the
 * quality-scaled result to that band (HOVER_CRUISE_MIN/MAX), so no craft
 * ever leaves it. Boost is a SEPARATE turbine surge above cruise (≈1.35×);
 * open water drags every craft down to HOVER_WATER_TOP (hover_physics).
 */

export interface HovercraftSpec {
  key: string;
  name: string;
  tier: 1 | 2 | 3 | 4 | 5;
  speedMps: number;   // base CRUISE top speed (quality-scaled, band-clamped; walking is 6)
  boostMps: number;   // boosted top speed (quality-scaled) — the SHIFT turbine ceiling above cruise
  accel: number;      // /s exponential chase toward the target velocity (time to ~96% cruise ≈ 2/accel s)
  hoverM: number;     // ride height over terrain/water
  desc: string;
}

/** The cruise band every ground vehicle lives in: 150 ± 40 m/s. */
/** 150 ± 40 m/s at scale 1 — see VEHICLE_SPEED_SCALE (hover_physics.ts): the band, the tops and the boost margin all scale. */
export const HOVER_CRUISE_MIN = 110 * VEHICLE_SPEED_SCALE;
export const HOVER_CRUISE_MAX = 190 * VEHICLE_SPEED_SCALE;

export const HOVERCRAFT: Record<string, HovercraftSpec> = {
  hover_skiff:   { key: 'hover_skiff',   name: 'Dust Skiff',     tier: 1, speedMps: 110, boostMps: 150, accel: 0.55, hoverM: 0.9, desc: 'A welded pallet on salvaged grav plates. It floats, mostly.' },
  hover_dune:    { key: 'hover_dune',    name: 'Dune Runner',    tier: 2, speedMps: 130, boostMps: 175, accel: 0.60, hoverM: 1.0, desc: 'The prospector’s workhorse — boxy, stubborn, tireless.' },
  hover_falcon:  { key: 'hover_falcon',  name: 'Sky Falcon',     tier: 3, speedMps: 150, boostMps: 200, accel: 0.65, hoverM: 1.1, desc: 'A swept-hull courier skimmer with a coil that sings.' },
  hover_raptor:  { key: 'hover_raptor',  name: 'Ridge Raptor',   tier: 4, speedMps: 170, boostMps: 230, accel: 0.70, hoverM: 1.2, desc: 'Twin coils, a lattice brain, and very little patience.' },
  hover_tempest: { key: 'hover_tempest', name: 'Storm Tempest',  tier: 5, speedMps: 190, boostMps: 255, accel: 0.75, hoverM: 1.3, desc: 'A storm core between crystal rails, aimed at the horizon. The pinnacle.' },
};

export function isHovercraft(itemKey: string): boolean {
  return itemKey in HOVERCRAFT;
}

/** Crafted quality (0..1000) scales CRUISE top speed 0.9× → 1.2×, then the
 *  result is clamped into the 150 ± 40 band (no vehicle leaves it). */
export function hovercraftSpeed(spec: HovercraftSpec, quality: number): number {
  const v = spec.speedMps * VEHICLE_SPEED_SCALE * (0.9 + Math.min(1000, Math.max(0, quality)) / 1000 * 0.3);
  return Math.min(HOVER_CRUISE_MAX, Math.max(HOVER_CRUISE_MIN, v));
}

/** Boosted (SHIFT) top speed — quality scales it a tighter 0.95× → 1.1× and it
 *  always sits ABOVE the crafted cruise (a real surge). Also the server's boost
 *  speed cap. */
export function hovercraftBoostSpeed(spec: HovercraftSpec, quality: number): number {
  const b = spec.boostMps * VEHICLE_SPEED_SCALE * (0.95 + Math.min(1000, Math.max(0, quality)) / 1000 * 0.15);
  return Math.max(b, hovercraftSpeed(spec, quality) + 20 * VEHICLE_SPEED_SCALE);
}
